import { DREEvolutionMonth } from '@/lib/api/financial/financial-accounting-api'
import { useContainerWidth } from '@/lib/hooks/use-container-width'
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

interface Props {
    months: DREEvolutionMonth[]
}

const CHART_HEIGHT = 280

const MONTH_LABELS = [
    'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
    'jul', 'ago', 'set', 'out', 'nov', 'dez',
]

/** '2026-07' -> 'jul/26' */
const monthLabel = (month: string) => {
    const [year, m] = month.split('-')
    return `${MONTH_LABELS[Number(m) - 1] ?? m}/${year.slice(2)}`
}

const currency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value)

const SERIES_LABELS: Record<string, string> = {
    netRevenue: 'Receita líquida',
    outflow: 'Custos e despesas',
    netIncome: 'Lucro líquido',
}

const SERIES_COLORS: Record<string, string> = {
    netRevenue: '#22c55e',
    outflow: '#f87171',
    netIncome: '#6366f1',
}

/**
 * Legenda em HTML em vez do <Legend> do Recharts: o CSS global
 * (`.recharts-legend-wrapper { position: relative !important }`, em index.css)
 * tira a legenda do posicionamento absoluto e colapsa o wrapper do gráfico
 * para largura zero. Nenhum gráfico do projeto usa <Legend> por isso.
 */
function ChartLegend() {
    return (
        <div className="mb-1 flex flex-wrap items-center gap-4 text-xs text-base-content/70">
            {Object.entries(SERIES_LABELS).map(([key, label]) => (
                <span key={key} className="flex items-center gap-1.5">
                    <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: SERIES_COLORS[key] }}
                    />
                    {label}
                </span>
            ))}
        </div>
    )
}

export default function DREEvolutionChart({ months }: Props) {
    const { ref, width } = useContainerWidth()

    if (months.length === 0) {
        return (
            <div className="flex items-center justify-center h-56 text-base-content/50 text-sm">
                Sem lançamentos no período para montar a evolução
            </div>
        )
    }

    const data = months.map(m => ({
        label: monthLabel(m.month),
        netRevenue: m.netRevenue / 100,
        // Saída do período: o que consome a receita antes do resultado.
        outflow: (m.costs + m.operatingExpenses) / 100,
        netIncome: m.netIncome / 100,
    }))

    return (
        <>
            <ChartLegend />
            <div
                ref={ref}
                className="w-full"
                style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}
            >
                {width > 0 && (
                <ComposedChart
                    data={data}
                    width={width}
                    height={CHART_HEIGHT}
                    margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                >
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        width={72}
                        tickFormatter={value =>
                            new Intl.NumberFormat('pt-BR', {
                                notation: 'compact',
                                maximumFractionDigits: 1,
                            }).format(Number(value) || 0)
                        }
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: 'oklch(var(--b1))',
                            border: '1px solid oklch(var(--b3))',
                            borderRadius: '8px',
                        }}
                        formatter={(value, name) => [
                            currency(Number(value) || 0),
                            SERIES_LABELS[String(name)] ?? String(name),
                        ]}
                    />
                    <Bar
                        dataKey="netRevenue"
                        fill={SERIES_COLORS.netRevenue}
                        radius={[4, 4, 0, 0]}
                    />
                    <Bar
                        dataKey="outflow"
                        fill={SERIES_COLORS.outflow}
                        radius={[4, 4, 0, 0]}
                    />
                    <Line
                        type="monotone"
                        dataKey="netIncome"
                        stroke={SERIES_COLORS.netIncome}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                    />
                </ComposedChart>
                )}
            </div>
        </>
    )
}
